import { useCallback, useEffect, useRef, useState } from "react";
import Cropper from "react-easy-crop";
import type { Area } from "react-easy-crop";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../helpers/api";
import { Panel } from "./Panel";
import Spinner from "./Spinners";
import { ShowToast } from "./toast";
import type {
  BrandAssetFamilyStatus,
  BrandAssetsStatus,
  CampaignPushRow,
  SearchImagePreview,
} from "../types/brand-assets";

const STATUS_STYLES: Record<BrandAssetFamilyStatus, string> = {
  sent: "bg-green-100 text-green-800 border-green-300",
  partial: "bg-amber-100 text-amber-800 border-amber-300",
  failed: "bg-red-100 text-red-700 border-red-300",
  pending: "bg-blue-100 text-blue-800 border-blue-300",
  not_eligible: "bg-gray-100 text-gray-600 border-gray-300",
  excluded: "bg-gray-100 text-gray-600 border-gray-300",
  off: "bg-gray-100 text-gray-500 border-gray-300",
};

const STATUS_LABELS: Record<BrandAssetFamilyStatus, string> = {
  sent: "Sent",
  partial: "Partially sent",
  failed: "Failed",
  pending: "Pending",
  not_eligible: "Not eligible yet",
  excluded: "Excluded (handled manually)",
  off: "Push disabled",
};

function formatDate(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleString(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  });
}

function campaignLabel(resourceName: string): string {
  const id = resourceName.split("/").pop();
  return id ? `Campaign ${id}` : resourceName;
}

function apiErrorMessage(err: unknown): string {
  const data = (err as any)?.response?.data;
  if (data?.reasons?.length) {
    return `${data.message}: ${data.reasons.join(", ").replace(/_/g, " ")}`;
  }
  if (typeof data?.message === "string") {
    return data.message.replace(/_/g, " ");
  }
  return err instanceof Error ? err.message : "Request failed";
}

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

// Matches the backend's logo target size, so the upload payload stays small
// and what the admin crops is exactly what gets pushed.
const LOGO_CROP_OUTPUT_PX = 1200;

async function cropToSquarePngDataUrl(
  imageSrc: string,
  area: Area,
): Promise<string> {
  const image = await new Promise<HTMLImageElement>((resolve, reject) => {
    const el = new Image();
    el.onload = () => resolve(el);
    el.onerror = () => reject(new Error("Could not load the image"));
    el.src = imageSrc;
  });
  const canvas = document.createElement("canvas");
  canvas.width = LOGO_CROP_OUTPUT_PX;
  canvas.height = LOGO_CROP_OUTPUT_PX;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Canvas not supported");
  ctx.drawImage(
    image,
    area.x,
    area.y,
    area.width,
    area.height,
    0,
    0,
    LOGO_CROP_OUTPUT_PX,
    LOGO_CROP_OUTPUT_PX,
  );
  return canvas.toDataURL("image/png");
}

function Lightbox({ url, onClose }: { url: string; onClose: () => void }) {
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onClose]);
  return (
    <div
      className="fixed inset-0 z-[10000] bg-black/85 flex items-center justify-center p-6 cursor-zoom-out"
      onClick={onClose}
    >
      <img src={url} alt="" className="max-w-full max-h-full object-contain" />
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        className="absolute top-4 right-4 w-9 h-9 rounded-full bg-white/15 text-white text-xl leading-none hover:bg-white/30"
      >
        ×
      </button>
    </div>
  );
}

function LogoCropModal({
  imageSrc,
  uploading,
  onCancel,
  onConfirm,
}: {
  imageSrc: string;
  uploading: boolean;
  onCancel: () => void;
  onConfirm: (pngDataUrl: string) => void;
}) {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [areaPixels, setAreaPixels] = useState<Area | null>(null);
  const onCropComplete = useCallback(
    (_area: Area, pixels: Area) => setAreaPixels(pixels),
    [],
  );

  return (
    <div className="fixed inset-0 z-[10000] bg-black/70 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg p-4">
        <div className="text-base font-bold text-content-primary mb-1">
          Crop logo
        </div>
        <div className="text-xs text-content-tertiary mb-3">
          Google renders the Business Logo inside a circle — keep the mark
          centred in the square.
        </div>
        <div className="relative w-full h-80 bg-neutral-900 rounded overflow-hidden">
          <Cropper
            image={imageSrc}
            crop={crop}
            zoom={zoom}
            aspect={1}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={onCropComplete}
          />
        </div>
        <div className="flex items-center gap-3 mt-3">
          <span className="text-xs text-content-secondary">Zoom</span>
          <input
            type="range"
            min={1}
            max={4}
            step={0.05}
            value={zoom}
            onChange={(e) => setZoom(Number(e.target.value))}
            className="flex-1"
          />
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <button
            type="button"
            onClick={onCancel}
            disabled={uploading}
            className="text-xs font-medium px-3 py-1.5 rounded border border-border-primary text-content-secondary hover:bg-neutral-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={uploading || !areaPixels}
            onClick={async () => {
              if (!areaPixels) return;
              try {
                onConfirm(await cropToSquarePngDataUrl(imageSrc, areaPixels));
              } catch (err) {
                ShowToast({
                  type: "error",
                  message:
                    err instanceof Error ? err.message : "Crop failed",
                });
              }
            }}
            className="text-xs font-medium px-3 py-1.5 rounded bg-brand-primary text-white disabled:opacity-50"
          >
            {uploading ? "Uploading…" : "Crop & upload"}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * The push-attempt copy: when (roughly) the cron will next try this family.
 * Deliberately says "attempt" — Google's review decides what's published.
 */
function attemptLabel(
  family: "gate1" | "gate2",
  eligibility: BrandAssetsStatus["eligibility"],
): string | null {
  if (!eligibility.cronArmed) return null;
  if (!eligibility.adsLive) {
    return family === "gate1"
      ? "Push attempt: first daily run after ads go live"
      : "Push attempt: about 6 days after ads go live";
  }
  if (family === "gate1" || eligibility.imageGateOpen) {
    return "Push attempt: next daily run (06:00 UTC)";
  }
  if (eligibility.imageGateOpensInDays != null) {
    const days = eligibility.imageGateOpensInDays;
    return `Push attempt: in about ${days} day${days === 1 ? "" : "s"}`;
  }
  return null;
}

function StatusChip({
  status,
  sentAt,
}: {
  status: BrandAssetFamilyStatus;
  sentAt?: string | null;
}) {
  return (
    <span
      className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full border text-xs font-medium whitespace-nowrap ${STATUS_STYLES[status]}`}
    >
      {STATUS_LABELS[status]}
      {status === "sent" && sentAt ? ` · ${formatDate(sentAt)}` : ""}
    </span>
  );
}

function AttemptNote({ label }: { label: string | null }) {
  if (!label) return null;
  return (
    <div className="mt-2 text-xs text-content-tertiary">
      {label} — we attempt the push; Google reviews every asset and approval
      isn't guaranteed.
    </div>
  );
}

function CampaignTable({ rows }: { rows: CampaignPushRow[] }) {
  if (!rows.length) return null;
  return (
    <table className="mt-3 w-full text-xs text-left">
      <thead>
        <tr className="text-content-tertiary border-b border-border-primary">
          <th className="py-1 pr-3 font-medium">Campaign</th>
          <th className="py-1 pr-3 font-medium">Status</th>
          <th className="py-1 pr-3 font-medium">Pushed at</th>
          <th className="py-1 font-medium">Last error</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr
            key={`${row.campaignResourceName}-${row.pushStatus}`}
            className="border-b border-border-primary/50 text-content-secondary"
          >
            <td className="py-1 pr-3 whitespace-nowrap">
              {campaignLabel(row.campaignResourceName)}
            </td>
            <td className="py-1 pr-3 capitalize">{row.pushStatus}</td>
            <td className="py-1 pr-3 whitespace-nowrap">
              {formatDate(row.pushedAt)}
            </td>
            <td className="py-1 text-red-700 break-all">
              {row.lastErrorMessage ?? "—"}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

const ORIGIN_LABELS: Record<SearchImagePreview["origin"], string> = {
  scraped: "Found",
  uploaded: "Customer",
  admin: "Admin",
};

function ImageTile({
  image,
  canDelete,
  onDelete,
  onView,
  deleting,
}: {
  image: SearchImagePreview;
  canDelete: boolean;
  onDelete: () => void;
  onView: () => void;
  deleting: boolean;
}) {
  return (
    <div className="w-36">
      <div
        className="relative w-36 h-36 rounded border border-border-primary bg-gray-50 overflow-hidden flex items-center justify-center group cursor-zoom-in"
        onClick={onView}
        title="View full screen"
      >
        <img
          src={image.url}
          alt=""
          loading="lazy"
          className="w-full h-full object-cover"
        />
        {canDelete && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onDelete();
            }}
            disabled={deleting}
            title="Remove from the push set"
            className="absolute top-1 right-1 w-6 h-6 rounded-full bg-black/60 text-white text-sm leading-none items-center justify-center hidden group-hover:flex hover:bg-red-600 disabled:opacity-50"
          >
            ×
          </button>
        )}
      </div>
      <div className="mt-1 flex items-center justify-between gap-1">
        <span className="text-[10px] uppercase tracking-wide text-content-tertiary">
          {ORIGIN_LABELS[image.origin]}
        </span>
        <span
          className={`text-[10px] font-medium capitalize ${
            image.pushStatus === "pushed"
              ? "text-green-700"
              : image.pushStatus === "failed"
                ? "text-red-700"
                : image.pushStatus === "partial"
                  ? "text-amber-700"
                  : "text-content-tertiary"
          }`}
          title={
            image.pushedVariants.length
              ? `Pushed variants: ${image.pushedVariants.join(", ")} · ${formatDate(image.pushedAt)}`
              : undefined
          }
        >
          {image.pushStatus}
        </span>
      </div>
    </div>
  );
}

function EligibilitySummary({
  eligibility,
}: {
  eligibility: BrandAssetsStatus["eligibility"];
}) {
  const facts: Array<{ label: string; value: string }> = [
    {
      label: "Auto-push cron",
      value: eligibility.cronArmed ? "Armed" : "Off",
    },
    {
      label: "Logo push",
      value: eligibility.logoPushArmed ? "Armed" : "Off",
    },
    {
      label: "Ads live",
      value: eligibility.adsLive
        ? `Yes · since ${formatDate(eligibility.adsLiveAt)}`
        : "Not yet",
    },
    {
      label: "Image slot open",
      value: eligibility.imageGateOpen
        ? "Yes"
        : eligibility.imageGateOpensInDays != null
          ? `In ~${eligibility.imageGateOpensInDays}d`
          : "Not yet",
    },
    {
      label: "Onboarded",
      value: `${formatDate(eligibility.fullyOnboardedAt)}${
        eligibility.withinCutoff ? "" : " (before auto-push cutoff)"
      }`,
    },
    {
      label: "Google Ads account",
      value: eligibility.googleAdsCustomerId ?? "Missing",
    },
  ];
  return (
    <div className="px-4 py-3">
      <div className="flex flex-wrap gap-x-8 gap-y-2">
        {facts.map((fact) => (
          <div key={fact.label}>
            <div className="text-[11px] uppercase tracking-wide text-content-tertiary">
              {fact.label}
            </div>
            <div className="text-sm text-content-primary">{fact.value}</div>
          </div>
        ))}
      </div>
      {eligibility.reasons.length > 0 && (
        <div className="mt-2 text-xs text-content-tertiary">
          Not auto-pushing yet:{" "}
          {eligibility.reasons.join(", ").replace(/_/g, " ")}
        </div>
      )}
      <div className="mt-2 text-xs text-content-tertiary">
        Eligible assets are pushed automatically — Business Name and Logo on
        the first daily run after ads go live, Search Images about 6 days
        later. We attempt the push; Google reviews every asset and decides
        what's published, so approval isn't guaranteed.
      </div>
    </div>
  );
}

function EditButton({
  onClick,
  children,
}: {
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="text-xs font-medium text-brand-primary hover:underline whitespace-nowrap"
    >
      {children}
    </button>
  );
}

export function BrandAssets({ userId }: { userId: string }) {
  const queryClient = useQueryClient();
  const { data, isLoading, error } = useQuery<BrandAssetsStatus>({
    queryKey: ["brand-assets", userId],
    queryFn: () => api.semProvisioning.getBrandAssets(userId),
    enabled: !!userId,
    refetchOnWindowFocus: false,
  });

  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState("");
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);
  const [logoCropSrc, setLogoCropSrc] = useState<string | null>(null);
  const logoInputRef = useRef<HTMLInputElement>(null);
  const imagesInputRef = useRef<HTMLInputElement>(null);

  const refresh = () =>
    queryClient.invalidateQueries({ queryKey: ["brand-assets", userId] });
  const onMutationError = (err: unknown) =>
    ShowToast({ type: "error", message: apiErrorMessage(err) });

  const saveName = useMutation({
    mutationFn: (businessName: string) =>
      api.semProvisioning.setBrandAssetBusinessName(userId, businessName),
    onSuccess: () => {
      setEditingName(false);
      refresh();
      ShowToast({ type: "success", message: "Business name updated." });
    },
    onError: onMutationError,
  });

  const uploadLogo = useMutation({
    mutationFn: (imageBase64: string) =>
      api.semProvisioning.uploadBrandAssetLogo(userId, imageBase64),
    onSuccess: () => {
      setLogoCropSrc(null);
      refresh();
      ShowToast({ type: "success", message: "Logo uploaded." });
    },
    onError: onMutationError,
  });

  const addImages = useMutation({
    mutationFn: (imagesBase64: string[]) =>
      api.semProvisioning.addBrandAssetSearchImages(userId, imagesBase64),
    onSuccess: () => {
      refresh();
      ShowToast({ type: "success", message: "Images added to the push set." });
    },
    onError: onMutationError,
  });

  const removeImage = useMutation({
    mutationFn: (imageUrl: string) =>
      api.semProvisioning.removeBrandAssetSearchImage(userId, imageUrl),
    onSuccess: () => {
      refresh();
      ShowToast({ type: "success", message: "Image removed." });
    },
    onError: onMutationError,
  });

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-40">
        <Spinner className="!text-brand-primary" />
      </div>
    );
  }
  if (error || !data) {
    return (
      <div className="text-sm text-content-tertiary px-4 py-8 text-center">
        Couldn't load brand assets for this user
        {error instanceof Error && error.message.includes("404")
          ? " (no onboarding metadata yet)."
          : "."}
      </div>
    );
  }

  const images = data.searchImages.images;
  const deletionAllowed =
    data.searchImages.editable &&
    images.length > data.searchImages.minimumImages;

  const onLogoPicked = async (file: File | undefined) => {
    if (!file) return;
    // Open the crop step first — the upload happens on "Crop & upload".
    setLogoCropSrc(await fileToBase64(file));
  };

  const onImagesPicked = async (files: FileList | null) => {
    if (!files?.length) return;
    addImages.mutate(
      await Promise.all(Array.from(files).map((file) => fileToBase64(file))),
    );
  };

  return (
    <div className="flex flex-col gap-4 pb-6">
      <Panel
        label="Google Ads eligibility"
        contentClassName="!overflow-visible"
      >
        <EligibilitySummary eligibility={data.eligibility} />
      </Panel>

      <Panel
        label="Business Name"
        actions={
          <div className="flex items-center gap-3">
            {data.businessName.editable && !editingName && (
              <EditButton
                onClick={() => {
                  setNameDraft(data.businessName.value ?? "");
                  setEditingName(true);
                }}
              >
                Edit
              </EditButton>
            )}
            <StatusChip
              status={data.businessName.status}
              sentAt={data.businessName.sentAt}
            />
          </div>
        }
      >
        <div className="px-4 py-3">
          {editingName ? (
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={nameDraft}
                onChange={(e) => setNameDraft(e.target.value)}
                maxLength={25}
                className="border border-border-primary rounded px-2 py-1 text-sm w-72"
                placeholder="Brand name (max 25 chars)"
              />
              <button
                type="button"
                disabled={saveName.isPending || !nameDraft.trim()}
                onClick={() => saveName.mutate(nameDraft)}
                className="text-xs font-medium px-3 py-1.5 rounded bg-brand-primary text-white disabled:opacity-50"
              >
                {saveName.isPending ? "Saving…" : "Save"}
              </button>
              <button
                type="button"
                onClick={() => setEditingName(false)}
                className="text-xs text-content-secondary hover:underline"
              >
                Cancel
              </button>
            </div>
          ) : (
            <div className="text-lg font-semibold text-content-primary">
              {data.businessName.value ?? "No hotel name on file"}
              {data.businessName.overridden && (
                <span className="ml-2 text-xs font-normal text-content-tertiary">
                  (admin override)
                </span>
              )}
            </div>
          )}
          {!data.businessName.valid && (
            <div className="mt-1 text-xs text-red-700">
              Fails Google's business-name rules (the push will skip it):{" "}
              {data.businessName.validationReasons
                .join(", ")
                .replace(/_/g, " ")}
            </div>
          )}
          {(data.businessName.status === "pending" ||
            data.businessName.status === "not_eligible") && (
            <AttemptNote label={attemptLabel("gate1", data.eligibility)} />
          )}
          <CampaignTable rows={data.businessName.perCampaign} />
        </div>
      </Panel>

      <Panel
        label="Business Logo"
        actions={
          <div className="flex items-center gap-3">
            {data.businessLogo.editable && (
              <>
                <input
                  ref={logoInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => {
                    onLogoPicked(e.target.files?.[0]);
                    e.target.value = "";
                  }}
                />
                <EditButton onClick={() => logoInputRef.current?.click()}>
                  {uploadLogo.isPending ? "Uploading…" : "Upload logo"}
                </EditButton>
              </>
            )}
            <StatusChip
              status={data.businessLogo.status}
              sentAt={data.businessLogo.sentAt}
            />
          </div>
        }
      >
        <div className="px-4 py-3 flex items-start gap-4">
          {data.businessLogo.previewUrl ? (
            <div
              className="w-24 h-24 rounded border border-border-primary bg-gray-50 overflow-hidden flex-shrink-0 cursor-zoom-in"
              onClick={() => setLightboxUrl(data.businessLogo.previewUrl)}
              title="View full screen"
            >
              <img
                src={data.businessLogo.previewUrl}
                alt="Business logo preview"
                className="w-full h-full object-contain"
              />
            </div>
          ) : (
            <div className="w-24 h-24 rounded border border-dashed border-border-primary flex items-center justify-center text-[11px] text-content-tertiary text-center px-2 flex-shrink-0">
              Not generated yet
            </div>
          )}
          <div className="min-w-0">
            <div className="text-sm text-content-secondary">
              {data.businessLogo.previewUrl
                ? `Generated ${formatDate(data.businessLogo.generatedAt)}.`
                : data.businessLogo.generationSkippedReason
                  ? `No compliant logo could be produced (${data.businessLogo.generationSkippedReason.replace(/_/g, " ")}) — upload one to fix.`
                  : "Generated when the customer completes the Highlights onboarding step, or upload one here."}
            </div>
            {(data.businessLogo.status === "pending" ||
              data.businessLogo.status === "not_eligible" ||
              data.businessLogo.status === "failed") && (
              <AttemptNote label={attemptLabel("gate1", data.eligibility)} />
            )}
            <CampaignTable rows={data.businessLogo.perCampaign} />
          </div>
        </div>
      </Panel>

      <Panel
        label={`Search Images (${images.length})`}
        actions={
          <div className="flex items-center gap-3">
            {data.searchImages.editable && (
              <>
                <input
                  ref={imagesInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  className="hidden"
                  onChange={(e) => {
                    onImagesPicked(e.target.files);
                    e.target.value = "";
                  }}
                />
                <EditButton onClick={() => imagesInputRef.current?.click()}>
                  {addImages.isPending ? "Uploading…" : "Add photos"}
                </EditButton>
              </>
            )}
            <StatusChip
              status={data.searchImages.status}
              sentAt={data.searchImages.sentAt}
            />
          </div>
        }
      >
        <div className="px-4 py-3">
          {images.length === 0 ? (
            <div className="text-sm text-content-tertiary">
              No reviewed photos yet — the customer curates these on the
              Highlights onboarding step, or add some here.
            </div>
          ) : (
            <div className="flex flex-wrap gap-4">
              {images.map((image) => (
                <ImageTile
                  key={image.url}
                  image={image}
                  canDelete={deletionAllowed && image.deletable}
                  deleting={removeImage.isPending}
                  onView={() => setLightboxUrl(image.url)}
                  onDelete={() => {
                    if (
                      window.confirm(
                        "Remove this image from the push set? It won't be sent to Google Ads.",
                      )
                    ) {
                      removeImage.mutate(image.url);
                    }
                  }}
                />
              ))}
            </div>
          )}
          {data.searchImages.editable &&
            images.length <= data.searchImages.minimumImages && (
              <div className="mt-2 text-xs text-content-tertiary">
                At least {data.searchImages.minimumImages} images must remain
                in the set, so deleting is disabled until more are added.
              </div>
            )}
          {(data.searchImages.status === "pending" ||
            data.searchImages.status === "not_eligible") && (
            <AttemptNote label={attemptLabel("gate2", data.eligibility)} />
          )}
        </div>
      </Panel>

      {lightboxUrl && (
        <Lightbox url={lightboxUrl} onClose={() => setLightboxUrl(null)} />
      )}
      {logoCropSrc && (
        <LogoCropModal
          imageSrc={logoCropSrc}
          uploading={uploadLogo.isPending}
          onCancel={() => setLogoCropSrc(null)}
          onConfirm={(pngDataUrl) => uploadLogo.mutate(pngDataUrl)}
        />
      )}
    </div>
  );
}
